"use client"

/**
 * Shared Chat View Component - Task-First Architecture
 *
 * Uses Task as the primary DAG context (not Thread):
 * - Task owns Thread (1:1 relationship)
 * - Steps are added via nested modification pattern
 * - Reuses existing Task instead of creating new Task per step
 *
 * Uses the reactive GraphStore pattern for automatic updates:
 * - useDAGSync subscribes to Task DAG and populates GraphStore
 * - Thread and Steps accessed via task.thread
 * - DAG patches automatically update UI via GraphStore atoms
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import ChatScroll from '@sourcepilot/ui/framework/ui/organisms/ChatScroll'
import NotFound from '@sourcepilot/ui/framework/ui/molecules/not-found'
import { ChatTaskContext } from '@sourcepilot/ui/framework/ui/molecules/chat-task-context'
import {
  TaskRetryNotice,
  RETRYABLE_TASK_STATUSES,
} from '@sourcepilot/ui/framework/ui/molecules/ModelRequestResponseUser'
import ChatInput from '@sourcepilot/ui/shell/chat/chatInput'
import { escStopsTheTurn } from '@sourcepilot/ui/shell/chat/esc-to-stop'
import { MessageSquarePlus, PanelRightClose } from 'lucide-react'
import { Button } from '@sourcepilot/ui/framework/ui/atoms/button'

import { useSimpleAuth } from "@sourcepilot/ui/shell/auth/use-simple-auth"
import { useDAGSync, useTaskDeltaStream } from '@sourcepilot/ui/framework/data/dag-sync/hooks'
import { useDagSyncSocket } from '@sourcepilot/ui/framework/data/hooks/use-dag-sync-socket'
import { useNode } from '@sourcepilot/framework/relationship-manager'
import { useEditable } from '@sourcepilot/ui/framework/data/hooks/use-editable'
import { useEditableNodes } from '@sourcepilot/ui/framework/data/hooks/use-editable-nodes'
import { useSetAtom, useAtom, useAtomValue } from 'jotai'
import { useDebugMode } from '@sourcepilot/ui/shell/auth/use-debug-mode'
import { chatTransitionAtom, useTabData, useChatCollapsed, pendingChatMessagesAtom, taskPausedAtom } from '@sourcepilot/ui/framework/state/session-data';
import { useStartProjectChat } from '../../data/hooks/use-start-project-chat';
import { useAppHost } from '@sourcepilot/ui/framework/state/AppHostContext';
import { ChatTask } from '@sourcepilot/generated/pdm_tasks/api/chattask';
import { Thread } from '@sourcepilot/generated/chat/api/thread';
import { Step } from '@sourcepilot/generated/chat/api/step';
import { ChatRoleType } from '@sourcepilot/generated/shared/enums';
import { ModelRegistry } from '@sourcepilot/generated/registries/model-registry';
import { useModelSwitch } from './model-switch';
import { ChatNavContext } from '@sourcepilot/ui/framework/data/url-helpers';
import { ProjectChatSwitcher } from '@sourcepilot/ui/shell/chat/ProjectChatSwitcher';
import { QueryNames } from '@sourcepilot/generated/query-names';


interface ChatViewProps {
  taskId: string;  // Task ID (primary DAG context)
  taskClassName?: string;  // Task class name (e.g., "ChatTask", "ChatSkillsTask") — defaults to ChatTask
  isTransitioning?: boolean;
}

export function ChatView({ taskId, taskClassName, isTransitioning = false }: ChatViewProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { user } = useSimpleAuth();
  const { isConnected } = useDagSyncSocket();
  const setIsTransitioning = useSetAtom(chatTransitionAtom);
  const [, setCollapsed] = useChatCollapsed(taskId);
  const { replaceActiveTab } = useTabData();
  const { currentEntityAtom, createNewChatTaskForCurrentEntity } = useAppHost();
  const currentEntity = useAtomValue(currentEntityAtom);
  // on a project page: a chat of the project's own (the entity is a product's)
  const projectChat = useStartProjectChat();

  // Initialize debug mode from URL params (?debug=1 or ?debug=0)
  useDebugMode();

  // One task-level delta subscription per open chat (masterplan/28 §4):
  // all part renderers below register handlers only — no per-part
  // subscribe round-trips, and reconnects resume from the last-seen
  // stream id.
  useTaskDeltaStream(taskId || null);

  // Resolve the task class dynamically — falls back to ChatTask
  const resolvedClassName = taskClassName || 'ChatTask';
  const TaskClass = useMemo(() => {
    if (taskClassName && ModelRegistry[taskClassName]) {
      return ModelRegistry[taskClassName];
    }
    return ChatTask;
  }, [taskClassName]);

  const [activeTask, setActiveTask] = useState<any | null>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [pendingMessages, setPendingMessages] = useAtom(pendingChatMessagesAtom);
  const [pendingMessageSent, setPendingMessageSent] = useState(false);

  // Message queue state - for handling submissions during processing
  const [queuedMessage, setQueuedMessage] = useState<string | null>(null);
  const [failedMessage, setFailedMessage] = useState<string | null>(null);
  const isSubmittingRef = useRef(false);

  // useDAGSync with the task class as the DAG context: the serving open
  // brings the task and the islands its load spec opens (sub-tasks, the
  // system prompt) — no client include spec
  const { rootNode: task, isLoading, error } = useDAGSync(
    taskId ? { context: TaskClass, context_id: taskId } : null,
  );

  // Get the root task instance from DAG sync
  useEffect(() => {
    if (!isLoading && !error && task) {
      setActiveTask(task);
    }
  }, [isLoading, error, task]);

  // Get thread from task (reactive via GraphStore)
  const thread = useNode(activeTask?.thread) as Thread | null;

  // Stable editable wrapper for the thread - used for optimistic step submission
  const threadEditable = useEditable(thread);

  // Get steps from thread with optimistic updates (pending adds/removes merged in)
  // useEditableNodes combines GraphStore reactivity with editable pending ops
  const stepsView = useEditableNodes<Step>((threadEditable)?.steps);
  const steps = useMemo(
    () => stepsView?.orderBy('created_at').value() || [],
    [stepsView]
  );
  const latestStep = steps[steps.length - 1];

  // Track processing state via Step.is_complete and presence of ID
  // Pending steps (no ID) are optimistic - not yet confirmed by server
  // Processing steps (has ID, is_complete: false) are being processed by LLM
  const isPending = latestStep && !latestStep.id;  // Optimistic step, no server ID yet
  // The step alone is NOT enough: methodology-gate continuations (and
  // resume/retry completions) run AFTER the step is marked complete, with
  // the TASK still status='running' — keying only off the step made the
  // Stop button vanish mid-continuation, leaving the user unable to halt
  // a looping run. Either signal means "the model is working".
  const isProcessing =
    (latestStep && latestStep.id && !latestStep.is_complete) ||
    activeTask?.status === 'running';
  const isPaused = activeTask?.status === 'paused';

  // Model switch (model-switch.ts): shows the model that runs and why; a
  // choice is saved on the task when it is made, never on send
  const saveModelPreference = useCallback(async (pref: 'primary' | 'backup') => {
    if (!activeTask) return;
    const editable = activeTask.makeEditable() as any;
    editable.model_preference = pref;
    await editable.save();
  }, [activeTask]);
  const modelSwitch = useModelSwitch(activeTask, taskId, Boolean(isProcessing), saveModelPreference);

  // Stop in-flight generation. We trust the server to flip Task.status
  // and the latest Step.is_complete; once that DAG patch arrives the
  // derived `isProcessing` / `isPending` go false naturally. No
  // client-side optimistic clear — the input *should* stay disabled
  // until the server confirms the task is no longer running, because
  // there's no message queueing on the server side.
  const handleStop = useCallback(() => {
    if (!taskId) return;
    const apiBase = process.env.NEXT_PUBLIC_SOURCEPILOT_URL || '';
    fetch(`${apiBase}/tasks/${taskId}/cancel`, {
      method: 'POST',
      credentials: 'include',
    }).catch((err) => {
      console.warn('Cancel request failed:', err);
    });
  }, [taskId]);

  // Esc stops the turn, as Stop does (Tom, 2026-10-20), whenever Stop is
  // offered: a key pressed with focus inside this chat that nothing else
  // inside it took (esc-to-stop.ts)
  const stopOffered = Boolean(isProcessing || isPending);
  const handleEscKey = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!escStopsTheTurn(e, stopOffered)) return;
    e.preventDefault();
    handleStop();
  }, [stopOffered, handleStop]);

  // Sync paused state to Jotai atom so deeply-nested ToolCallPartRendererUser can read it
  const setTaskPaused = useSetAtom(taskPausedAtom);
  useEffect(() => {
    setTaskPaused({ isPaused: !!isPaused, task: isPaused ? activeTask : null });
    return () => setTaskPaused({ isPaused: false, task: null });
  }, [isPaused, activeTask, setTaskPaused]);

  // Get attached entity from task (if any) for redirects
  const attachedEntity = useNode(activeTask?.currently_attached_to);

  // Context-search scopes for the chat "@" picker. RFLP chats search the
  // RFLP domain (the default PDM `search-context-items` query is scoped to
  // SysMLElement and finds nothing here). Project scope is the default;
  // the project id comes from the attached Project. Falls back to
  // workspace-only until the attached entity resolves.
  const contextSearchScopes = useMemo(() => {
    if (resolvedClassName !== 'RFLPTask') return undefined;
    const projectId =
      (attachedEntity as any)?._label === 'Project'
        ? ((attachedEntity as any)?.id as string | undefined)
        : undefined;
    const scopes: Array<{ key: string; label: string; queryName: string; params?: Record<string, any> }> = [];
    if (projectId) {
      scopes.push({
        key: 'project',
        label: 'This project',
        queryName: QueryNames.rflpTasksSearchRflpContextItemsProject,
        params: { project_id: projectId },
      });
    }
    scopes.push({
      key: 'workspace',
      label: 'Workspace',
      queryName: QueryNames.rflpTasksSearchRflpContextItemsWorkspace,
    });
    return scopes;
  }, [resolvedClassName, attachedEntity]);

  // Handle pending messages from WorkspaceContent
  // When a new chat is created, the initial message is stored in an atom
  // Once we have the thread, send the pending message
  useEffect(() => {
    const pendingMessage = pendingMessages[taskId];
    if (!pendingMessage || pendingMessageSent) return;
    if (!activeTask || !thread) return;

    // Mark as sent immediately to prevent duplicate sends
    setPendingMessageSent(true);

    // Send the pending message
    console.log(`📤 Sending pending message for task ${taskId}:`, pendingMessage.text);

    // Use async IIFE to handle the promise
    (async () => {
      try {
        await newStep(pendingMessage.text);
        console.log(`✅ Pending message sent successfully`);

        // Clear from atom after successful send
        setPendingMessages(prev => {
          const { [taskId]: _, ...rest } = prev;
          return rest;
        });
      } catch (error) {
        console.error('❌ Failed to send pending message:', error);
        // Reset sent flag to allow retry
        setPendingMessageSent(false);
      }
    })();
  }, [taskId, pendingMessages, activeTask, thread, pendingMessageSent]);

  // Redirect to entity page if thread is attached and we're on the standalone chat page
  useEffect(() => {
    // Only redirect if we're on the standalone chat page route
    if (!pathname?.includes('/chat/')) return;
    if (!taskId || !attachedEntity) return;

    // Thread is attached to an entity - replace chat tab with entity tab
    const entityId = (attachedEntity as any).id;
    const entityType = (attachedEntity as any)._label;

    // Map entity type to content type
    let contentType: 'product' | 'action' | 'part' | 'projects' | null = null;
    let subpath: string | undefined = undefined;

    if (entityType === 'PDMProduct') {
      contentType = 'product';
      subpath = '/breakdown';
    } else if (entityType === 'PDMAction') {
      contentType = 'action';
    } else if (entityType === 'PDMPart') {
      contentType = 'part';
    } else if (entityType === 'Project') {
      // RFLP project — land on the custom project page (/projects/[id]) with
      // the chat docked in the side panel.
      contentType = 'projects';
    }

    if (contentType && taskId) {
      console.log(`[ChatView] Task attached to ${contentType}, replacing tab`);
      // Shrink the fullscreen chat into the side panel as we redirect.
      setIsTransitioning('shrink');

      // For an RFLP project the agent param must ride along so the panel
      // reopens this RFLP chat. Both params go in the subpath (NOT
      // `taskClassName`) because `replaceActiveTab` appends `agent` with a
      // fresh `?`, which would double-`?` a subpath that already has `chat`.
      const chatQuery =
        entityType === 'Project'
          ? `?chat=${taskId}&agent=${resolvedClassName}`
          : `?chat=${taskId}`;

      // Replace the chat tab with the entity tab
      replaceActiveTab({
        id: entityId as string,
        title: (attachedEntity as any).name || entityType,
        contentType: contentType,
        subpath: `${subpath || ''}${chatQuery}`,
      });
    }
  }, [attachedEntity, taskId, pathname, resolvedClassName, setIsTransitioning, replaceActiveTab]);

  /**
   * Internal function to actually send a step to the server.
   * Separated from handleSend to allow queue processing to call it directly.
   */
  const submitStep = useCallback(async (txt: string, contextIds?: string[]): Promise<boolean> => {
    if (!activeTask || !threadEditable) {
      console.error('Cannot add step: task or thread editable not ready');
      return false;
    }

    isSubmittingRef.current = true;

    // Create step with is_complete: false for processing state detection
    // Optimistic steps don't have an ID until server confirms
    // Include context_items if provided (IDs of @mentioned items)
    const step = new Step({
      text: txt,
      role: ChatRoleType.USER,
      is_complete: false,  // Mark as not complete (will be set by server when done)
      ...(contextIds && contextIds.length > 0 && { context_items: contextIds }),
    });

    // Add step via stable editable wrapper (optimistic - appears immediately)
    const stepsManager = (threadEditable as any).steps;
    stepsManager.add(step);

    // Wrap in task modification for nested PATCH
    const taskEditable = activeTask.makeEditable() as any;
    taskEditable.thread.modify(threadEditable);

    try {
      const uuidMap = await taskEditable.save();

      // Clear pending ops with uuid_map - this maps localUUIDs to real server IDs
      // so we can promote optimistic items to GraphStore with correct IDs
      stepsManager.clearPendingOps(uuidMap);

      isSubmittingRef.current = false;
      return true;
    } catch (error) {
      // Clear pending ops on error - don't show failed optimistic step
      stepsManager.clearPendingOps();
      taskEditable.discard();
      console.error('❌ Failed to add step:', error);
      isSubmittingRef.current = false;
      return false;
    }
  }, [activeTask, threadEditable]);

  /**
   * Handle user message submission with queue support.
   * - If already processing: queue the message (append with newline if already queued)
   * - If not processing: submit immediately
   * - On failure: restore message to input
   */
  const handleSend = useCallback(async (txt: string, contextIds?: string[]) => {
    // Clear any previous failed message when user sends a new one
    setFailedMessage(null);

    // If currently processing or submitting, queue the message
    // Note: contextIds are not preserved in queue (would need more complex state)
    if (isProcessing || isPending || isSubmittingRef.current) {
      setQueuedMessage(prev => prev ? `${prev}\n${txt}` : txt);
      console.log('📋 Message queued (processing in progress):', txt);
      return;
    }

    // Submit immediately with context IDs
    const success = await submitStep(txt, contextIds);
    if (!success) {
      // On failure, restore message to input
      setFailedMessage(txt);
      console.log('⚠️ Message failed, restored to input:', txt);
    }
  }, [isProcessing, isPending, submitStep]);

  // Watch for processing to complete and send queued message
  useEffect(() => {
    // Only process queue when not processing, not pending, and not submitting
    if (isProcessing || isPending || isSubmittingRef.current) return;
    if (!queuedMessage) return;

    // Processing finished and we have a queued message - send it
    console.log('🚀 Processing complete, sending queued message:', queuedMessage);
    const messageToSend = queuedMessage;
    setQueuedMessage(null);

    (async () => {
      const success = await submitStep(messageToSend);
      if (!success) {
        // On failure, restore message to input
        setFailedMessage(messageToSend);
        console.log('⚠️ Queued message failed, restored to input:', messageToSend);
      }
    })();
  }, [isProcessing, isPending, queuedMessage, submitStep]);

  // Legacy newStep function for backward compatibility with pendingMessages effect
  async function newStep(txt: string) {
    return handleSend(txt);
  }

  // Error must be checked BEFORE the `!activeTask` loading state — a
  // 404 / "not found" from the backend produces error+no-activeTask
  // simultaneously, and we want the soft-404 UI, not "Loading task..."
  // forever. Backend sends `{type: "error", code: 404, message: "<X>
  // not found"}` over the WS when the context_id doesn't resolve; that
  // flows through useDAGSync's handleError → setError.
  //
  // For the "not found" case we use the shared <NotFound /> molecule
  // (also used by the catch-all route at `app/(main)/not-found.tsx`).
  if (error) {
    const notFound = /not found/i.test(error);
    if (notFound) {
      return (
        <NotFound
          title="Chat not found"
          description="This chat doesn't exist. It may have been deleted, or the URL might be wrong."
        />
      );
    }
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="text-center text-red-500">
          <h2 className="text-lg font-semibold">Error loading task</h2>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // Show loading only if we don't have activeTask yet
  if (!activeTask) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading task...</p>
        </div>
      </div>
    );
  }

  return (
    <ChatNavContext.Provider value={{ chatId: taskId, agent: resolvedClassName }}>
    {/* focusable (-1) so a click anywhere in the chat keeps the focus in it,
        and Esc reaches this chat's handler (the composer is off while a
        turn waits on a question) */}
    <div className="h-full flex flex-col w-full outline-none" tabIndex={-1} onKeyDown={handleEscKey} data-testid="chat-view">
      {/* Task/Thread Header */}
      <div className={`flex justify-between items-center gap-1 border-b-2 border-black h-[38px] mx-[6px]`}>
        <div className="flex min-w-0 flex-1 items-center justify-start text-sm text-left text-gray-800 py-[5px] pl-[4px]">
          {thread?.name && (
            <span className="max-w-[40ch] truncate inline-block">
              {thread.name}
            </span>
          )}
          {isPending && (
            <span className="ml-2 text-xs text-gray-400 animate-pulse">
              Sending...
            </span>
          )}
          {isProcessing && !isPaused && (
            <span className="ml-2 text-xs text-gray-500 animate-pulse">
              Processing...
            </span>
          )}
          {isPaused && (
            <span className="ml-2 text-xs text-amber-600">
              Awaiting your response
            </span>
          )}
        </div>
        <div className="flex min-w-0 items-center gap-1">
          {/* Per-project chat switcher (chat icon + project name; a dropdown
              when the project has multiple chats). Rendered in-flow here, not
              floating, so it can't overlap the buttons. Self-hides off a
              project route. Allowed to shrink/truncate so the buttons below
              stay on-screen when the panel is dragged narrow. */}
          <div className="min-w-0">
            <ProjectChatSwitcher inline />
          </div>
          {/* New Chat button — creates a new task of the resolved class
              attached to the current entity. The host-supplied factory
              owns the domain-coupled steps (Task.save(),
              currently_attached_to.set(currentEntity), re-save). */}
          {(projectChat || currentEntity) && (
            <Button
              variant="ghost"
              size="sm"
              className="p-1 h-7 shrink-0 text-gray-600 hover:text-gray-900"
              title={projectChat?.failed ? 'The chat could not be started. Try again.' : 'New Chat'}
              data-testid="chat-new-chat"
              disabled={projectChat?.busy}
              onClick={async () => {
                if (projectChat) { await projectChat.start(); return; }
                const newTask = await createNewChatTaskForCurrentEntity({
                  taskClassName: resolvedClassName,
                });
                if (newTask?.id) {
                  // Navigate to the new chat with agent param for deep-link support
                  const searchParams = new URLSearchParams(window.location.search);
                  searchParams.set('chat', newTask.id);
                  if (resolvedClassName !== 'ChatTask') {
                    searchParams.set('agent', resolvedClassName);
                  }
                  router.push(`${pathname}?${searchParams.toString()}`);
                }
              }}
            >
              <MessageSquarePlus className="h-4 w-4" />
            </Button>
          )}
          {/* Collapse the side panel down to a compact bar. Keeps the chat
              in the URL (per-chat-id collapse state) so the thread context
              is preserved and ProjectChatAutoOpen doesn't re-open it. */}
          <Button
            variant="ghost"
            size="sm"
            className="p-1 h-7 shrink-0 text-gray-600 hover:text-gray-900"
            title="Collapse chat"
            onClick={() => setCollapsed(true)}
          >
            <PanelRightClose className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {thread ? (
        <div className="flex flex-col h-[calc(100%-38px)]">
          {/* Chat messages - using flex-grow to fill available space */}
          <div className="flex-grow overflow-hidden">
            <ChatTaskContext.Provider value={{ taskId, isWorking: isProcessing || isPending }}>
              <ChatScroll
                thread={thread}
                steps={steps}
              />
            </ChatTaskContext.Provider>
          </div>

          {/* Task-status-driven retry notice: covers turns that died
              BETWEEN responses (no incomplete trailing ModelResponse for
              the response-level fallback to hang a notice on). Hidden as
              soon as activity resumes — the retry flips the status. */}
          {RETRYABLE_TASK_STATUSES.has(activeTask?.status ?? '') &&
            !isProcessing &&
            !isPending && <TaskRetryNotice taskId={taskId} />}

          {/* Chat input - fixed at bottom */}
          <div className="w-full p-[8px] border-t border-gray-800 bg-white border-l border-l-gray-200">
            {/* Text input + send button */}
            <ChatInput
              chatSlug={thread?.id || null}
              value={failedMessage || undefined}
              onSend={handleSend}
              onValueChange={() => setFailedMessage(null)}
              setAutoScroll={setAutoScroll}
              canAddContext={true}
              contextSearchScopes={contextSearchScopes}
              isProcessing={isProcessing || isPending}
              isPaused={!!isPaused}
              queuedMessage={queuedMessage}
              modelSwitch={
                modelSwitch.options.length > 1
                  ? {
                      options: modelSwitch.options, value: modelSwitch.value, onChange: modelSwitch.choose,
                      notice: modelSwitch.notice, onOpen: modelSwitch.onOpen,
                    }
                  : undefined
              }
              onStop={handleStop}
            />
          </div>
        </div>
      ) : (
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center text-gray-500">
            <p>Thread not found or no messages yet</p>
            <div className="mt-4 text-xs">
              <p>Connected: {isConnected ? 'Yes' : 'No'}</p>
              <p>Task ID: {taskId}</p>
            </div>
          </div>
        </div>
      )}
    </div>
    </ChatNavContext.Provider>
  );
}
